/**
 * KeyboardShortcuts - Keyboard shortcuts panel and handler
 *
 * Key features:
 * - Categorized shortcuts configuration
 * - OS-aware key display (Cmd on Mac, Ctrl on Windows/Linux)
 * - Accessible modal panel with focus trap
 * - "?" key to toggle panel visibility
 *
 * Implementation: Phase 2 Task 5.0
 *
 * Usage:
 *   const shortcuts = new KeyboardShortcuts()
 *   shortcuts.initialize()  // Sets up "?" key handler
 *   shortcuts.show()        // Open panel
 *   shortcuts.hide()        // Close panel
 *   shortcuts.destroy()     // Cleanup
 */

// Detect macOS for key display
const isMac = typeof navigator !== 'undefined' &&
  /Mac|iPod|iPhone|iPad/.test(navigator.platform)

// Modifier key display based on OS
const MOD_KEY = isMac ? '⌘' : 'Ctrl'
const _ALT_KEY = isMac ? '⌥' : 'Alt' // eslint-disable-line no-unused-vars
const SHIFT_KEY = '⇧'

/**
 * Shortcuts configuration organized by category
 * Each shortcut has: key (display), description
 */
export const SHORTCUTS = {
  general: {
    label: 'General',
    items: [
      { key: '?', description: 'Show keyboard shortcuts' },
      { key: 'Esc', description: 'Close panel / Cancel editing' },
      { key: `${MOD_KEY}+S`, description: 'Save all changes' },
      { key: `${MOD_KEY}+Z`, description: 'Undo last change' },
      { key: `${MOD_KEY}+${SHIFT_KEY}+Z`, description: 'Redo' }
    ]
  },
  editing: {
    label: 'Text Editing',
    items: [
      { key: `${MOD_KEY}+B`, description: 'Bold text' },
      { key: `${MOD_KEY}+I`, description: 'Italic text' },
      { key: `${MOD_KEY}+K`, description: 'Insert/edit link' },
      { key: `${MOD_KEY}+A`, description: 'Select all' },
      { key: `${MOD_KEY}+C`, description: 'Copy' },
      { key: `${MOD_KEY}+V`, description: 'Paste (plain text)' }
    ]
  },
  navigation: {
    label: 'Navigation',
    items: [
      { key: 'Tab', description: 'Move to next field' },
      { key: `${SHIFT_KEY}+Tab`, description: 'Move to previous field' },
      { key: 'Enter', description: 'Confirm / Submit' },
      { key: '↑ ↓', description: 'Navigate list items' }
    ]
  }
}

export class KeyboardShortcuts {
  constructor(options = {}) {
    this.options = {
      ...options
    }

    this.panel = null
    this.overlay = null
    this.isVisible = false
    this.previousFocus = null

    // Bound handlers for cleanup
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleOverlayClick = this.handleOverlayClick.bind(this)
  }

  /**
   * Initialize the shortcuts handler
   */
  initialize() {
    document.addEventListener('keydown', this.handleKeydown)
    console.log('[KeyboardShortcuts] Initialized')
  }

  /**
   * Handle keydown for "?" to show panel and Escape to close
   * @private
   */
  handleKeydown(e) {
    // Check if we're in an input context where shortcuts shouldn't trigger
    const target = e.target
    const isEditable = target.isContentEditable ||
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT'

    // "?" key (Shift+/) to show shortcuts panel
    if (e.key === '?' && !isEditable) {
      e.preventDefault()
      this.toggle()
      return
    }

    // Escape to close panel
    if (e.key === 'Escape' && this.isVisible) {
      e.preventDefault()
      e.stopPropagation()
      this.hide()
      return
    }

    // A11y FIX: Proper focus trap when panel is open
    if (e.key === 'Tab' && this.isVisible && this.panel) {
      this.handleFocusTrap(e)
    }
  }

  /**
   * Handle focus trap - cycle through focusable elements in the panel
   * @private
   */
  handleFocusTrap(e) {
    const focusableSelectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    const focusableElements = this.panel.querySelectorAll(focusableSelectors)
    const focusable = Array.from(focusableElements).filter((el) => !el.disabled && el.offsetParent !== null)

    if (focusable.length === 0) {
      e.preventDefault()
      return
    }

    const firstFocusable = focusable[0]
    const lastFocusable = focusable[focusable.length - 1]

    if (e.shiftKey) {
      // Shift+Tab: if on first element, wrap to last
      if (document.activeElement === firstFocusable) {
        e.preventDefault()
        lastFocusable.focus()
      }
    } else if (document.activeElement === lastFocusable) {
      // Tab: if on last element, wrap to first
      e.preventDefault()
      firstFocusable.focus()
    }
    // Otherwise, let browser handle normal Tab navigation within the panel
  }

  /**
   * Handle click on overlay to close panel
   * @private
   */
  handleOverlayClick(e) {
    if (e.target === this.overlay) {
      this.hide()
    }
  }

  /**
   * Toggle panel visibility
   */
  toggle() {
    if (this.isVisible) {
      this.hide()
    } else {
      this.show()
    }
  }

  /**
   * Show the shortcuts panel
   */
  show() {
    if (this.isVisible) return

    // Store current focus for restoration
    this.previousFocus = document.activeElement

    // Create panel if needed
    if (!this.panel) {
      this.createPanel()
    }

    // Show overlay and panel
    this.overlay.style.display = 'flex'
    this.isVisible = true

    // Focus the close button for accessibility
    const closeBtn = this.panel.querySelector('.shortcuts-close-btn')
    if (closeBtn) {
      closeBtn.focus()
    }

    console.log('[KeyboardShortcuts] Panel shown')
  }

  /**
   * Hide the shortcuts panel
   */
  hide() {
    if (!this.isVisible) return

    this.overlay.style.display = 'none'
    this.isVisible = false

    // Restore previous focus
    if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
      this.previousFocus.focus()
    }

    console.log('[KeyboardShortcuts] Panel hidden')
  }

  /**
   * Create the panel DOM structure
   * @private
   */
  createPanel() {
    // Create overlay (backdrop for click-outside-to-close)
    this.overlay = document.createElement('div')
    this.overlay.className = 'shortcuts-overlay'
    this.overlay.addEventListener('click', this.handleOverlayClick)

    // A11y FIX: role="dialog" goes on the panel container, not the overlay
    this.panel = document.createElement('div')
    this.panel.className = 'shortcuts-panel'
    this.panel.setAttribute('role', 'dialog')
    this.panel.setAttribute('aria-modal', 'true')
    this.panel.setAttribute('aria-labelledby', 'shortcuts-title')

    // Header
    const header = document.createElement('div')
    header.className = 'shortcuts-header'
    header.innerHTML = `
      <h2 id="shortcuts-title" class="shortcuts-title">Keyboard Shortcuts</h2>
      <button type="button" class="shortcuts-close-btn" aria-label="Close shortcuts panel">
        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    `
    this.panel.appendChild(header)

    // Close button handler
    const closeBtn = header.querySelector('.shortcuts-close-btn')
    closeBtn.addEventListener('click', () => this.hide())

    // Content - render each category
    const content = document.createElement('div')
    content.className = 'shortcuts-content'

    for (const [_categoryKey, category] of Object.entries(SHORTCUTS)) {
      const section = document.createElement('div')
      section.className = 'shortcuts-category'

      const categoryLabel = document.createElement('h3')
      categoryLabel.className = 'shortcuts-category-label'
      categoryLabel.textContent = category.label
      section.appendChild(categoryLabel)

      const list = document.createElement('div')
      list.className = 'shortcuts-list'

      for (const shortcut of category.items) {
        const item = document.createElement('div')
        item.className = 'shortcut-item'

        const desc = document.createElement('span')
        desc.className = 'shortcut-description'
        desc.textContent = shortcut.description

        const keys = document.createElement('span')
        keys.className = 'shortcut-keys'
        // Split by + and wrap each in kbd
        const keyParts = shortcut.key.split('+')
        keyParts.forEach((part, i) => {
          const kbd = document.createElement('kbd')
          kbd.textContent = part.trim()
          keys.appendChild(kbd)
          if (i < keyParts.length - 1) {
            keys.appendChild(document.createTextNode(' + '))
          }
        })

        item.appendChild(desc)
        item.appendChild(keys)
        list.appendChild(item)
      }

      section.appendChild(list)
      content.appendChild(section)
    }

    this.panel.appendChild(content)

    // Footer hint
    const footer = document.createElement('div')
    footer.className = 'shortcuts-footer'
    footer.innerHTML = `<span class="shortcuts-hint">Press <kbd>?</kbd> to toggle this panel</span>`
    this.panel.appendChild(footer)

    // Add panel to overlay, overlay to document
    this.overlay.appendChild(this.panel)
    document.body.appendChild(this.overlay)
  }

  /**
   * Clean up and remove the shortcuts panel
   */
  destroy() {
    document.removeEventListener('keydown', this.handleKeydown)

    if (this.overlay && this.overlay.parentNode) {
      this.overlay.removeEventListener('click', this.handleOverlayClick)
      this.overlay.parentNode.removeChild(this.overlay)
    }

    this.panel = null
    this.overlay = null
    this.isVisible = false
    this.previousFocus = null

    console.log('[KeyboardShortcuts] Destroyed')
  }
}
